import type { ComponentChildren } from 'preact'
import type { CompanyProfileDto } from '@/api/companies'
import { formatDate } from '@/components/contacts/contactFormat'

/**
 * Everything the global company document knows.
 *
 * Every block is conditional. Most of these fields are schema-nullable and the enrichment prompt
 * is told to return null rather than guess, so they are frequently absent even on a healthy
 * company — a thinly-known company should render a shorter page, never a scaffold of empty labels.
 */
export function CompanyAboutTab({ profile }: { profile: CompanyProfileDto }) {
  // The server initializes these to empty lists, but a field it omits deserializes to undefined
  // here and `.length` would take the whole page down over a missing keyword array. This tab's
  // entire premise is that most of these are absent, so coalescing is the honest default.
  const products = profile.ProductsAndServices ?? []
  const coreValues = profile.CoreValues ?? []
  const keywords = profile.Keywords ?? []
  const specialities = profile.Specialities ?? []
  const emailDomains = profile.EmailDomains ?? []

  const hasNarrative =
    profile.Summary ||
    products.length > 0 ||
    profile.ValueProposition ||
    profile.MissionStatement ||
    coreValues.length > 0 ||
    keywords.length > 0

  // Exactly the fields inside the Firmographics <dl>, and nothing else. Specialities and email
  // domains render in their own blocks below, so counting them here would put a Firmographics
  // heading over an empty list — the scaffold of empty labels this file exists to avoid.
  const hasFirmographics =
    profile.PrimaryIndustry ||
    profile.StaffCountRange ||
    profile.StaffCount ||
    profile.FoundedYear ||
    profile.Headquarters ||
    profile.FollowerCount ||
    profile.Website

  const hasFacts =
    hasFirmographics ||
    specialities.length > 0 ||
    emailDomains.length > 0

  if (!hasNarrative && !hasFacts) {
    return (
      <div class="px-5 py-10 text-center">
        <div class="font-medium text-slate-700 dark:text-slate-200">Nothing on file yet</div>
        <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
          This company hasn't been enriched. Details appear here once its website has been read.
        </div>
      </div>
    )
  }

  // Two-up only when both halves have something. A company with LinkedIn firmographics but no
  // website narrative — common, since the site may never have been read — otherwise leaves an
  // empty column and the facts floating at the far right of a wide page.
  const columns = hasNarrative && hasFacts
    ? 'grid-cols-1 lg:grid-cols-[1.5fr_1fr]'
    : 'grid-cols-1'

  return (
    <div class={`grid ${columns} gap-6 px-5 py-5`}>
      <div>
        {profile.Summary && (
          <Block label="Summary">
            <p class="text-slate-600 dark:text-slate-300 max-w-[68ch]">{profile.Summary}</p>
          </Block>
        )}
        {products.length > 0 && (
          <Block label="Products and services">
            <p class="text-slate-600 dark:text-slate-300 max-w-[68ch]">
              {products.join(', ')}
            </p>
          </Block>
        )}
        {profile.ValueProposition && (
          <Block label="Value proposition">
            <p class="text-slate-600 dark:text-slate-300 max-w-[68ch]">{profile.ValueProposition}</p>
          </Block>
        )}
        {profile.MissionStatement && (
          <Block label="Mission">
            <p class="text-slate-600 dark:text-slate-300 max-w-[68ch]">{profile.MissionStatement}</p>
          </Block>
        )}
        {coreValues.length > 0 && (
          <Block label="Core values">
            <Chips items={coreValues} />
          </Block>
        )}
        {keywords.length > 0 && (
          <Block label="Keywords">
            <Chips items={keywords} />
          </Block>
        )}
      </div>

      <div>
        {hasFirmographics && (
          <Block label="Firmographics">
            <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              <Fact label="Industry" value={profile.PrimaryIndustry} />
              <Fact label="Staff" value={profile.StaffCountRange || profile.StaffCount?.toLocaleString()} />
              <Fact label="Founded" value={profile.FoundedYear?.toString()} />
              <Fact label="Headquarters" value={profile.Headquarters} />
              <Fact label="Followers" value={profile.FollowerCount?.toLocaleString()} />
              {/* Host only. The stored value carries a full path often enough that it becomes the
                  widest row on the page while adding nothing. */}
              <Fact label="Website" value={hostOf(profile.Website)} />
            </dl>
          </Block>
        )}
        {specialities.length > 0 && (
          <Block label="Specialities">
            <Chips items={specialities} />
          </Block>
        )}
        {emailDomains.length > 0 && (
          <Block label="Known email domains">
            <Chips items={emailDomains} />
          </Block>
        )}

        {(profile.WebsiteLastScrapedUtc || profile.LastEnrichedUtc || !hasNarrative) && (
          <div class="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800 text-2xs text-slate-400 dark:text-slate-500">
            {/* Say why the page is thin. Everything above the firmographics comes from reading the
                company's own site, so when that has not happened the tab looks broken rather than
                empty — and roughly one attempt in five fails outright. */}
            {!profile.WebsiteLastScrapedUtc && (
              <div class="mb-1">
                {profile.Website ? `We haven't read ${hostOf(profile.Website)} yet.` : "We haven't read this company's website yet."}
              </div>
            )}
            {profile.WebsiteLastScrapedUtc && <>Website last read {formatDate(profile.WebsiteLastScrapedUtc)}</>}
            {profile.WebsiteLastScrapedUtc && profile.LastEnrichedUtc && ' · '}
            {profile.LastEnrichedUtc && <>Enriched {formatDate(profile.LastEnrichedUtc)}</>}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The bare host, so a stored URL with a path doesn't dominate a definition list.
 *
 * The fallback matters as much as the happy path: enrichment writes this field, so it can hold
 * something URL parsing rejects, and returning the raw value there would put the long string back
 * in the row this helper exists to shorten. Cut to the first separator instead.
 */
function hostOf(url?: string | null): string | undefined {
  const trimmed = url?.trim()
  if (!trimmed) return undefined
  try {
    return new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`).hostname.replace(/^www\./, '')
  } catch {
    return trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').split(/[/?#]/)[0].replace(/^www\./, '') || trimmed
  }
}

function Block({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="mb-4 last:mb-0">
      <div class="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {label}
      </div>
      {children}
    </div>
  )
}

function Fact({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <>
      <dt class="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd class="m-0 text-slate-800 dark:text-slate-100 break-words">{value}</dd>
    </>
  )
}

function Chips({ items }: { items: string[] }) {
  return (
    <div class="flex flex-wrap gap-1.5">
      {items.map(item => (
        <span
          key={item}
          class="rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 text-2xs text-slate-600 dark:text-slate-300"
        >
          {item}
        </span>
      ))}
    </div>
  )
}
